import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";

import { InfoCircleTwoTone } from "@ant-design/icons";
import { notification } from "antd";

import { LOCAL_STORAGE } from "@constants";
import { cardExclusiveUrl, mainUrl } from "@settings";

import { AuthLoading } from "@components/Auth/Loading";
import Link from "@components/Link";
import { useApiAuthSelectCorban } from "@hooks/api/auth";
import useAuth from "@hooks/useAuth";
import { useClientSettings } from "@hooks/useClientSettings";
import { CorbanOption, useCorbansOptions } from "@hooks/useCorbansOptions";
import useFeatureFlags from "@hooks/useFeatureFlags";
import { isCardProductType } from "@utils/card";

type FavoritesProps = {
  corbanId: string;
  userId?: NonNullable<ReturnType<typeof useAuth>["user"]>["id"] | null;
  favorites?: CorbanContext["corbanFavorites"];
  action?: "add" | "remove" | "set";
};

type CorbanContext = {
  corbans: CorbanOption[];
  corbanFavorites: Array<{ path: string; name: string }>;
  selectedCorban: CorbanOption | null;
  onSelectCorban: (corban: CorbanOption) => void;
  addFavorites: (favorites: CorbanContext["corbanFavorites"]) => void;
  removeFavorites: (favorites: CorbanContext["corbanFavorites"]) => void;
  loading: boolean;
  isFetched: boolean;
  isReady: boolean;
  needCorbanSelection: boolean;
  corbanNeedsRedirection: (corban: CorbanOption) => boolean;
};

export const CorbanContext = createContext<CorbanContext>({
  corbans: [],
  corbanFavorites: [],
  selectedCorban: null,
  onSelectCorban: () => {
    //
  },
  addFavorites: () => {
    //
  },
  removeFavorites: () => {
    //
  },
  loading: false,
  isFetched: false,
  isReady: false,
  needCorbanSelection: false,
  corbanNeedsRedirection: () => false,
});

const hasNonCardProduct = (corban: CorbanOption) => {
  return corban.produtos?.some((product) => {
    return !isCardProductType(product.tipo_produto);
  });
};

const getCorbanUrl = (
  corban: CorbanOption,
  enableCorrespondenteBanqueiro: boolean,
) => {
  if (enableCorrespondenteBanqueiro && corban.url) return corban.url;
  if (hasNonCardProduct(corban)) {
    return mainUrl;
  } else {
    return cardExclusiveUrl;
  }
};

const buildCorbanUrl = (
  corban: CorbanOption,
  enableCorrespondenteBanqueiro: boolean,
) => {
  if (!corban) return "";
  const url = getCorbanUrl(corban, enableCorrespondenteBanqueiro);
  const corbanUrl = new URL(`/?cid=${corban.id}`, url);
  return corbanUrl.href;
};

const useDefaultSelectedCorbanId = () => {
  const [search] = useSearchParams();
  const { onLogout } = useAuth();

  const [cid, setCID] = useState(() => search.get("cid"));
  const corbanId = !!cid && parseInt(cid);

  const reset = useCallback(() => setCID(null), []);

  useEffect(() => {
    if (corbanId) onLogout();
  }, [onLogout, corbanId]);

  return [corbanId || null, reset] as const;
};

export const CorbanContextProvider = ({ children }: PropsWithChildren) => {
  const { featureFlags } = useFeatureFlags();
  const client = useClientSettings();
  const [notificationApi, contextHolder] = notification.useNotification();

  const [redirectingCorban, setRedirectingCorban] = useState<CorbanOption>();
  const [favorites, setFavorites] = useState<CorbanContext["corbanFavorites"]>(
    [],
  );
  const isRedirecting = !!redirectingCorban;
  const { user, onLogout, setCorbanProducts, updateUser } = useAuth();
  const isAuthenticated = !!user?.accessToken;
  const enableCorrespondenteBanqueiro =
    !!featureFlags.enableCorrespondenteBanqueiro;

  const {
    options,
    loading: isLoadingOptions,
    isFetched,
  } = useCorbansOptions({
    skipFetch: isRedirecting || !isAuthenticated,
  });

  const [defaultCorbanId, resetDefaultCorban] = useDefaultSelectedCorbanId();
  const defaultCorban = useMemo(
    () =>
      !isAuthenticated || !defaultCorbanId
        ? null
        : (options.find((o) => o.id === defaultCorbanId) ?? null),
    [defaultCorbanId, isAuthenticated, options],
  );

  const selectedCorbanId = user?.corbanId;
  const selectedCorban = useMemo(
    () => options.find((o) => o.id === selectedCorbanId) ?? null,
    [selectedCorbanId, options],
  );
  const [selectCorban, { loading: isLoadingCorbanSelection }] =
    useApiAuthSelectCorban();

  const changeFavorites = useEvent(
    ({ userId, corbanId, favorites: fav = [], action }: FavoritesProps) => {
      const item = localStorage.getItem(LOCAL_STORAGE.FAVORITES + `-${userId}`);
      const storageFavorites = item
        ? (JSON.parse(item) as Record<
            FavoritesProps["corbanId"],
            CorbanContext["corbanFavorites"]
          >)
        : {};
      let corbanFavorites = storageFavorites?.[corbanId] ?? [];

      switch (action) {
        case "add": {
          const newFav = fav.filter(
            (f) =>
              !corbanFavorites?.some(
                (cf) =>
                  cf.path.replace(/\/$/, "") === f.path.replace(/\/$/, ""),
              ),
          );
          corbanFavorites.push(...newFav);
          break;
        }
        case "remove": {
          const alterFav = corbanFavorites?.filter(
            (cf) =>
              !fav.some(
                (f) => cf.path.replace(/\/$/, "") === f.path.replace(/\/$/, ""),
              ),
          );
          corbanFavorites = alterFav;
          break;
        }
        case "set":
        default:
          break;
      }

      if ("add" === action || "remove" === action) {
        const data: Record<
          FavoritesProps["corbanId"],
          CorbanContext["corbanFavorites"]
        > = { [corbanId]: corbanFavorites };
        localStorage.setItem(
          LOCAL_STORAGE.FAVORITES + `-${userId}`,
          JSON.stringify({
            ...storageFavorites,
            ...data,
          }),
        );
      }

      setFavorites(corbanFavorites);
    },
  );

  const addFavorites = useCallback(
    (fav: NonNullable<FavoritesProps["favorites"]>) => {
      if (!user?.id || !selectedCorban?.id || !fav) return;
      changeFavorites({
        userId: user.id,
        corbanId: selectedCorban.id.toString(),
        favorites: fav,
        action: "add",
      });
    },
    [changeFavorites, user?.id, selectedCorban?.id],
  );

  const removeFavorites = useCallback(
    (fav: NonNullable<FavoritesProps["favorites"]>) => {
      if (!user?.id || !selectedCorban?.id || !fav) return;
      changeFavorites({
        userId: user.id,
        corbanId: selectedCorban.id.toString(),
        favorites: fav,
        action: "remove",
      });
    },
    [changeFavorites, user?.id, selectedCorban?.id],
  );

  const onSelectCorban = useCallback(
    (corban: CorbanOption) => {
      updateUser({
        corbanId: corban.id,
        corbanName: corban.name,
      });
    },
    [updateUser],
  );

  const redirectTo = useCallback(
    (url?: string) => {
      if (!url) return;
      onLogout(url);
    },
    [onLogout],
  );

  const notify = useCallback(() => {
    if (!user?.name) return;
    notificationApi.info({
      icon: <InfoCircleTwoTone />,
      message: `Olá, ${user.name}!`,
      description: (
        <>
          Você está logado no ambiente: <strong>{user.corbanName}</strong>
        </>
      ),
    });
  }, [notificationApi, user?.corbanName, user?.name]);

  const updateTokenWithCorban = useEvent(async () => {
    if (!selectedCorban || !isAuthenticated || isLoadingCorbanSelection) return;
    if (user.tokenHasDataOfCorbanId === selectedCorban.id) return;

    const { data } = await selectCorban({
      data: { id: selectedCorban.id },
    });

    updateUser({
      accessToken: data.access,
      refreshToken: data.refresh,
      tokenHasDataOfCorbanId: selectedCorban.id,
    });
  });

  const corbanNeedsRedirection = useCallback(
    (corban: CorbanOption) => {
      const url = getCorbanUrl(corban, enableCorrespondenteBanqueiro);
      return !!(url && url !== location.origin);
    },
    [enableCorrespondenteBanqueiro],
  );

  const isReady = !!(
    user?.tokenHasDataOfCorbanId &&
    user.tokenHasDataOfCorbanId === selectedCorban?.id
  );

  const needCorbanSelection =
    !selectedCorban && !defaultCorban && options?.length > 1;

  const isLoadingCorbanToken = !needCorbanSelection && !isReady;

  const isLoadingDefaultCorban =
    defaultCorban && defaultCorban !== selectedCorban;

  const loading =
    !user ||
    !isFetched ||
    isLoadingOptions ||
    isLoadingDefaultCorban ||
    isLoadingCorbanToken;

  const contextState = useMemo<CorbanContext>(
    () => ({
      loading,
      isFetched,
      isReady,
      selectedCorban,
      onSelectCorban,
      corbans: options,
      needCorbanSelection,
      corbanNeedsRedirection,
      corbanFavorites: favorites,
      addFavorites,
      removeFavorites,
    }),
    [
      needCorbanSelection,
      isFetched,
      isReady,
      selectedCorban,
      onSelectCorban,
      loading,
      options,
      corbanNeedsRedirection,
      favorites,
      addFavorites,
      removeFavorites,
    ],
  );

  useEffect(() => {
    if (selectedCorban || !isAuthenticated) return;
    if (options.length === 1) {
      onSelectCorban(options[0]);
    } else if (defaultCorban) {
      onSelectCorban(defaultCorban);
      resetDefaultCorban();
    }
  }, [
    options,
    selectedCorban,
    defaultCorban,
    isAuthenticated,
    onSelectCorban,
    resetDefaultCorban,
  ]);

  useEffect(() => {
    if (!user?.corbanId || !selectedCorban) return;

    if (corbanNeedsRedirection(selectedCorban)) {
      setRedirectingCorban(selectedCorban);
      redirectTo(buildCorbanUrl(selectedCorban, enableCorrespondenteBanqueiro));
    } else {
      void updateTokenWithCorban();
    }
  }, [
    selectedCorban,
    user?.corbanId,
    updateTokenWithCorban,
    redirectTo,
    corbanNeedsRedirection,
    enableCorrespondenteBanqueiro,
  ]);

  useEffect(() => {
    if (user?.id && selectedCorban?.id) {
      changeFavorites({
        action: "set",
        userId: user.id,
        corbanId: selectedCorban.id.toString(),
      });
    }
  }, [changeFavorites, selectedCorban?.id, user?.id]);

  useEffect(() => {
    if (!isReady || !selectedCorban || isRedirecting) return;
    setCorbanProducts(selectedCorban?.produtos ?? []);
    notify();
  }, [
    isReady,
    selectedCorban,
    isRedirecting,
    setCorbanProducts,
    notify,
    user?.accessToken,
  ]);

  const apiUrlV2 = localStorage.getItem("apiUrlOriginacao") === "v2";

  return (
    <CorbanContext.Provider value={contextState}>
      <Helmet>
        <link rel="icon" type="image/svg+xml" href={client.assets.favicon} />
        <title>{apiUrlV2 ? `v2 - ${client.name}` : client.name}</title>
      </Helmet>
      {isRedirecting ? (
        <AuthLoading
          title={`Redirecionando para "${redirectingCorban.name}"`}
          subTitle={
            <>
              <span>Você está sendo redirecionado para </span>
              <Link
                to={buildCorbanUrl(
                  redirectingCorban,
                  enableCorrespondenteBanqueiro,
                )}
              >
                {getCorbanUrl(redirectingCorban, enableCorrespondenteBanqueiro)}
              </Link>
            </>
          }
        />
      ) : (
        <>{children}</>
      )}
      {contextHolder}
    </CorbanContext.Provider>
  );
};
